import { useMsal } from '@azure/msal-react'
import axios, { AxiosError, RawAxiosRequestHeaders } from 'axios'
import { toast } from 'react-toastify'

import { apiConstants } from 'constants/APIConstants'
import { localStorageConstants } from 'constants/localstorageConstants'
import { getFromStorage, siteLogout } from 'services/helper'

export type Method = 'get' | 'post' | 'delete' | 'put' | 'patch'

const { AUTH_TOKEN } = localStorageConstants

const controller = new AbortController()

export const API_URL = process.env?.REACT_APP_NODE_URL ?? ''

export const useRequest = () => {
  const { instance } = useMsal()
  const {
    CUSTOMERS_API,
    TICKET_EXPORT_EXCEL,
    TICKET_EXPORT_CSV,
    TICKET_DETAILS,
    TICKET_PRINT_PDF,
    SUPPORT_EMAIL,
    // TICKETS_LIST_API,
  } = apiConstants

  /**
   * Handle logout
   */
  const handleLogout = (url = '') => {
    controller.abort()
    const showAlert: string[] = [
      CUSTOMERS_API,
      TICKET_EXPORT_EXCEL,
      TICKET_EXPORT_CSV,
      TICKET_DETAILS,
      TICKET_PRINT_PDF,
      SUPPORT_EMAIL,
    ]
    if (
      showAlert.includes(url) ||
      url.includes(CUSTOMERS_API) ||
      url.includes(TICKET_EXPORT_EXCEL) ||
      url.includes(TICKET_EXPORT_CSV) ||
      url.includes(TICKET_DETAILS) ||
      url.includes(TICKET_PRINT_PDF) ||
      url.includes(SUPPORT_EMAIL)
    ) {
      toast.error('Unauthorized access')
    }
    return siteLogout(instance)
  }

  /**
   * API call
   * @param url
   * @param method
   * @param data
   */
  const request = async (url: string, method: Method, params = {}, apiUrl = '', isPayload = false) => {
    try {
      const AUTH = getFromStorage(AUTH_TOKEN)
      axios.defaults.headers['authorization'] = AUTH ? `Bearer ${AUTH}` : ''
      const { data } = await axios[method](`${apiUrl || API_URL}${url}`, isPayload ? params : { params }, {
        signal: controller.signal,
      })
      return Promise.resolve(data)
    } catch (error) {
      const err = error as AxiosError
      if (err?.response?.status === 401) {
        return handleLogout(url)
      }
      return Promise.reject(error)
    }
  }

  /**
   * File Download
   * @param url
   * @param method
   * @param apiUrl
   * @param contentType
   * @param params
   * @param isPayload
   */
  const fileDownload = async (
    url: string,
    method: Method,
    apiUrl = API_URL,
    contentType = 'blob',
    params = {},
    isPayload = false,
  ) => {
    try {
      const headers: RawAxiosRequestHeaders = { 'Content-Type': contentType }
      const AUTH = getFromStorage(AUTH_TOKEN)
      axios.defaults.headers['authorization'] = AUTH ? `Bearer ${AUTH}` : ''

      const { data } = await axios[method](`${apiUrl}${url}`, {
        responseType: 'arraybuffer',
        ...(isPayload && { params }),
        signal: controller.signal,
        headers,
      })

      return Promise.resolve(data)
    } catch (error) {
      const err = error as AxiosError
      if (err?.response?.status === 401) {
        return handleLogout(url)
      }
      return Promise.reject(error)
    }
  }

  return { request, fileDownload }
}
